import type { Resolver } from 'react-hook-form';
import { z } from 'zod';
import type { CreditApplicationFormValues, Disclosure } from '../types';
import { stripDigits } from '../utils/masks';

const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
const dobMaskedRegex = /^\d{2}-\d{2}-\d{4}$/;
const phoneDigitsRegex = /^\d{10}$/;
const ssnRegex = /^(\d{4}|\d{9})$/;
const zipRegex = /^\d{5}(-\d{4})?$/;

const MIN_DOB_YEAR = 1900;
const MIN_APPLICANT_AGE_YEARS = 16;

type DobError = 'invalid' | 'tooYoung';

const validateDob = (value: string): DobError | null => {
  if (!dobMaskedRegex.test(value)) return 'invalid';
  const [month, day, year] = value.split('-').map(Number);
  if (year < MIN_DOB_YEAR) return 'invalid';
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'invalid';
  }
  if (new Date().getFullYear() - year < MIN_APPLICANT_AGE_YEARS) return 'tooYoung';
  return null;
};

const requiredString = (key: string) => z.string().min(1, key);

const addDobIssueIfInvalid = (value: string, ctx: z.RefinementCtx) => {
  const error = validateDob(value);
  if (!error) return;
  ctx.addIssue({
    code: 'custom',
    message:
      error === 'tooYoung'
        ? 'creditApplication.errors.dobTooYoung'
        : 'creditApplication.errors.dobInvalid',
  });
};

const requiredDobSchema = z
  .string()
  .min(1, 'creditApplication.errors.dobRequired')
  .superRefine(addDobIssueIfInvalid);

const optionalDobSchema = z.string().superRefine((v, ctx) => {
  if (v === '') return;
  addDobIssueIfInvalid(v, ctx);
});

const buildApplicantSchema = ({ requireDob }: { requireDob: boolean }) =>
  z.object({
    firstName: requiredString('creditApplication.errors.firstNameRequired'),
    middleName: z.string().optional().or(z.literal('')),
    lastName: requiredString('creditApplication.errors.lastNameRequired'),
    email: z
      .string()
      .min(1, 'creditApplication.errors.emailRequired')
      .regex(emailRegex, 'creditApplication.errors.emailInvalid'),
    phoneNumber: z
      .string()
      .min(1, 'creditApplication.errors.phoneRequired')
      .refine((v) => phoneDigitsRegex.test(stripDigits(v)), 'creditApplication.errors.phoneInvalid'),
    dob: requireDob ? requiredDobSchema : optionalDobSchema,
    ssn: z
      .string()
      .min(1, 'creditApplication.errors.ssnRequired')
      .refine((v) => ssnRegex.test(stripDigits(v)), 'creditApplication.errors.ssnInvalid'),
  });

const AddressSchema = z.object({
  address1: requiredString('creditApplication.errors.addressRequired'),
  address2: z.string().optional().or(z.literal('')),
  city: requiredString('creditApplication.errors.cityRequired'),
  state: requiredString('creditApplication.errors.stateRequired'),
  zip: z
    .string()
    .min(1, 'creditApplication.errors.zipRequired')
    .regex(zipRegex, 'creditApplication.errors.zipInvalid'),
});

const emptyApplicantSchema = z.object({
  firstName: z.string().optional().or(z.literal('')),
  middleName: z.string().optional().or(z.literal('')),
  lastName: z.string().optional().or(z.literal('')),
  email: z.string().optional().or(z.literal('')),
  phoneNumber: z.string().optional().or(z.literal('')),
  dob: z.string().optional().or(z.literal('')),
  ssn: z.string().optional().or(z.literal('')),
});

const buildBaseSchema = ({ requireDob }: { requireDob: boolean }) => {
  const applicantSchema = buildApplicantSchema({ requireDob });
  return z
    .object({
      primary: applicantSchema,
      hasSecondaryApplicant: z.boolean(),
      secondary: emptyApplicantSchema,
      address: AddressSchema,
      disclosures: z.record(z.string(), z.boolean().optional()),
    })
    .superRefine((data, ctx) => {
      if (!data.hasSecondaryApplicant) return;
      const result = applicantSchema.safeParse(data.secondary);
      if (result.success) return;
      for (const issue of result.error.issues) {
        ctx.addIssue({ ...issue, path: ['secondary', ...issue.path] });
      }
    });
};

export const buildCreditApplicationFormSchema = (
  disclosures: Disclosure[],
  { requireDob }: { requireDob: boolean },
) =>
  buildBaseSchema({ requireDob }).superRefine((data, ctx) => {
    for (const disclosure of disclosures) {
      if (!disclosure.requireConsent) continue;
      if (data.disclosures[disclosure.id] === true) continue;
      ctx.addIssue({
        code: 'custom',
        message: 'creditApplication.errors.disclosureRequired',
        path: ['disclosures', disclosure.id],
      });
    }
  });

export type CreditApplicationFormSchema = ReturnType<typeof buildCreditApplicationFormSchema>;

export const buildCreditApplicationFormResolver = (
  disclosures: Disclosure[],
  { requireDob }: { requireDob: boolean },
): Resolver<CreditApplicationFormValues> => {
  const schema = buildCreditApplicationFormSchema(disclosures, { requireDob });
  return async (values) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data as CreditApplicationFormValues, errors: {} };
    }
    const errors: Record<string, unknown> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      if (!path) continue;
      setNestedError(errors, issue.path as (string | number)[], { type: issue.code, message: issue.message });
    }
    return { values: {} as CreditApplicationFormValues, errors: errors as never };
  };
};

const setNestedError = (target: Record<string, unknown>, path: (string | number)[], value: unknown) => {
  let current: Record<string, unknown> = target;
  for (let i = 0; i < path.length - 1; i++) {
    const key = String(path[i]);
    if (typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  const last = String(path[path.length - 1]);
  if (current[last] === undefined) {
    current[last] = value;
  }
};
